import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { API_URL } from '../config';
import eventEmitter, { EVENTS } from '../utils/eventEmitter';
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Chip,
  LinearProgress,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tooltip,
  Skeleton,
  InputAdornment,
  useTheme,
  alpha,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Movie as MovieIcon,
  PlayCircle as PlayCircleIcon,
  Stop as StopIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';

function Projects() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [newProject, setNewProject] = useState({
    projectName: '',
    projectType: 'professional',
    academicInfo: {
      institution: '',
      department: '',
      advisor: '',
      gradYear: '',
      term: ''
    },
    logline: '',
    synopsis: '',
    genre: 'Drama',
    phase: 'Development',
    budget: '',
    teamSize: '',
    director: '',
    status: 'active',
  });

  const genres = [
    'Drama', 'Comedy', 'Horror', 'Action', 'Documentary',
    'Animation', 'Thriller', 'Romance', 'Sci-Fi', 'Fantasy',
  ];

  const phases = [
    'Development', 'Pre-Production', 'Production',
    'Post-Production', 'Distribution'
  ];

  const projectTypes = [
    { value: 'professional', label: 'Professional Project', icon: <MovieIcon /> },
    { value: 'capstone', label: 'Capstone Project', icon: <SchoolIcon /> },
    { value: 'thesis', label: 'Thesis Film', icon: <SchoolIcon /> },
    { value: 'student', label: 'Student Project', icon: <SchoolIcon /> }
  ];

  // Removed unused academic helper arrays



  useEffect(() => {
    fetchProjects();
  }, []);

  const filterAndSortProjects = React.useCallback(() => {
    let filtered = [...projects];
    console.log('Initial projects:', filtered);

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.logline?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.genre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.academicInfo?.institution?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.academicInfo?.advisor?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('After search filter:', filtered);
    }

    // Apply phase filter
    if (filterPhase !== 'all') {
      filtered = filtered.filter(project => project.phase === filterPhase);
      console.log('After phase filter:', filtered);
    }

    // Apply project type filter
    if (filterType !== 'all') {
      console.log('Filtering by project type:', filterType);
      filtered = filtered.filter(project => {
        console.log('Project:', project.projectName, 'Type:', project.projectType);
        return project.projectType === filterType;
      });
      console.log('After type filter:', filtered);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.projectName.localeCompare(b.projectName);
        case 'phase':
          return phases.indexOf(a.phase) - phases.indexOf(b.phase);
        case 'type':
          return a.projectType.localeCompare(b.projectType);
        case 'status':
          return (b.status === 'active') - (a.status === 'active');
        case 'date':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    setFilteredProjects(filtered);
  }, [projects, searchTerm, filterPhase, filterType, sortBy]);

  useEffect(() => {
    filterAndSortProjects();
  }, [filterAndSortProjects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/projects`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Server returns an array of projects directly
      setProjects(response.data);
      setFilteredProjects(response.data);
      setErrors([]); // Clear any existing errors
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setErrors(['Failed to load projects. Please try again.']);
      setProjects([]);
      setFilteredProjects([]);
    } finally {
      setLoading(false);
    }
  };

  

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setErrors([]); // Clear previous errors
    
    try {
      // Clean up academic info by removing empty values
      const cleanAcademicInfo = newProject.projectType !== 'professional' ? {
        ...newProject.academicInfo,
        institution: newProject.academicInfo.institution || undefined,
        department: newProject.academicInfo.department || undefined,
        advisor: newProject.academicInfo.advisor || undefined,
        gradYear: newProject.academicInfo.gradYear || undefined,
        term: newProject.academicInfo.term || undefined // Only include term if it has a value
      } : undefined;

      // Remove undefined values from academic info
      const academicInfo = cleanAcademicInfo ? Object.fromEntries(
        Object.entries(cleanAcademicInfo).filter(([_, v]) => v !== undefined)
      ) : undefined;

      // Prepare the project data
      const projectData = {
        ...newProject,
        academicInfo: Object.keys(academicInfo || {}).length > 0 ? academicInfo : undefined,
        genre: newProject.genre || 'Drama',
        phase: newProject.phase || 'Development',
        status: newProject.status || 'active'
      };

      console.log('Final project data being sent:', projectData);

      const response = await axios.post(`${API_URL}/api/v1/projects`, projectData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Server response:', response.data);
      
      setOpen(false);
      fetchProjects();
      setNewProject({
        projectName: '',
        projectType: 'professional',
        academicInfo: {
          institution: '',
          department: '',
          advisor: '',
          gradYear: '',
          term: ''
        },
        logline: '',
        synopsis: '',
        genre: 'Drama',
        phase: 'Development',
        budget: '',
        teamSize: '',
        director: '',
        status: 'active',
      });
    } catch (error) {
      console.error('Error creating project:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response) {
        // The server responded with an error
        const errorData = error.response.data;
        if (errorData.errors) {
          // Handle validation errors
          const errorMessages = Object.entries(errorData.errors)
            .filter(([_, message]) => message) // Filter out null messages
            .map(([field, message]) => `${field}: ${message}`);
          setErrors(errorMessages);
        } else {
          // Handle other server errors
          setErrors([errorData.message || 'Failed to create project']);
        }
      } else if (error.request) {
        // The request was made but no response was received
        setErrors(['No response from server. Please check your connection.']);
      } else {
        // Something happened in setting up the request
        setErrors(['Failed to send request. Please try again.']);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Input change:', name, value);
    
    if (name.startsWith('academicInfo.')) {
      const field = name.split('.')[1];
      setNewProject(prev => ({
        ...prev,
        academicInfo: {
          ...prev.academicInfo,
          [field]: value
        }
      }));
    } else {
      setNewProject(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    try {
      const deletedProjectId = projectToDelete._id;
      const deletedProject = projectToDelete;

      await axios.delete(`${API_URL}/api/v1/projects/${deletedProjectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProjects(prev => prev.filter(p => p._id !== deletedProjectId));
      setFilteredProjects(prev => prev.filter(p => p._id !== deletedProjectId));
      eventEmitter.emit(EVENTS.PROJECT_DELETED, {
        projectId: deletedProjectId,
        project: deletedProject
      });
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      setErrors(['Failed to delete project']);
    }
  };

  // Removed unused validateForm

  const getPhaseColor = (phase) => {
    switch (phase?.toLowerCase()) {
      case 'development':
        return '#FF9800'; // Yellow/Orange
      case 'pre-production':
        return '#4CAF50'; // Green
      case 'production':
        return '#2196F3'; // Blue
      case 'post-production':
        return '#9C27B0'; // Purple
      case 'distribution':
        return '#009688'; // Teal
      default:
        return '#757575'; // Grey
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, width: '100%', maxWidth: 'none', mx: 0 }}>
      {/* Minimalistic Header */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MovieIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
          </Box>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontSize: '1rem',
            color: 'text.primary'
          }}>
            Projects
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {filteredProjects.length}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ 
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            py: 0.5
          }}
        >
          New Project
        </Button>
      </Box>

      {/* Enhanced Search and Filters */}
      <Box 
        sx={{ 
          mb: 2, 
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200, maxWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1,
              fontSize: '0.8rem',
              height: 32,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.15)
              }
            }
          }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            displayEmpty
            renderValue={(v) => v === 'all' ? 'Type' : projectTypes.find(t => t.value === v)?.label.replace(' Project', '')}
            sx={{
              borderRadius: 1,
              fontSize: '0.8rem',
              height: 32,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.15)
              }
            }}
          >
            <MenuItem value="all">All Types</MenuItem>
            {projectTypes.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={filterPhase}
            onChange={(e) => setFilterPhase(e.target.value)}
            displayEmpty
            renderValue={(v) => v === 'all' ? 'Phase' : v}
            sx={{
              borderRadius: 1,
              fontSize: '0.8rem',
              height: 32,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.15)
              }
            }}
          >
            <MenuItem value="all">All Phases</MenuItem>
            {phases.map(phase => (
              <MenuItem key={phase} value={phase}>
                {phase}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            renderValue={(v) => 'Sort: ' + (v === 'date' ? 'Date' : v === 'name' ? 'Name' : v.charAt(0).toUpperCase() + v.slice(1))}
            sx={{
              borderRadius: 1,
              fontSize: '0.8rem',
              height: 32,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.15)
              }
            }}
          >
            <MenuItem value="date">Date Created</MenuItem>
            <MenuItem value="name">Project Name</MenuItem>
            <MenuItem value="type">Project Type</MenuItem>
            <MenuItem value="phase">Phase</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {errors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ))}
        </Box>
      )}

      {/* Projects Grid */}
      <Grid container spacing={2}>
        {loading ? (
          // Loading skeletons
          [...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            </Grid>
          ))
        ) : filteredProjects.length === 0 ? (
          // Enhanced Empty state
          <Grid item xs={12}>
            <Box 
              sx={{ 
                py: 5, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
                px: 2
              }}
            >
              <MovieIcon sx={{ fontSize: '3rem', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No Projects Found
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: '500px', mb: 2 }}>
                {searchTerm || filterPhase !== 'all' || filterType !== 'all'
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Start your filmmaking journey by creating your first project.'}
              </Typography>
              {!(searchTerm || filterPhase !== 'all' || filterType !== 'all') && (
                <Button
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => setOpen(true)}
                  sx={{ 
                    borderRadius: 1,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: theme.palette.mode === 'dark' ?
                      `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` :
                      '0 4px 12px rgba(0,0,0,0.1)',
                    background: theme.palette.mode === 'dark' ?
                      `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)` :
                      'linear-gradient(90deg, #1a237e 0%, #0d47a1 100%)',
                    '&:hover': {
                      background: theme.palette.mode === 'dark' ?
                        `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)` :
                        'linear-gradient(90deg, #0d47a1 0%, #1a237e 100%)',
                      boxShadow: theme.palette.mode === 'dark' ?
                        `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}` :
                        '0 6px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  Create New Project
                </Button>
              )}
            </Box>
          </Grid>
        ) : (
          // Enhanced Project cards
          filteredProjects.map(project => (
            <Grid item xs={12} sm={6} md={3} key={project._id}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: theme.palette.mode === 'dark' ? 
                    `1px solid ${alpha(theme.palette.divider, 0.2)}` : 
                    `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  boxShadow: theme.palette.mode === 'dark' ?
                    '0 2px 8px rgba(0,0,0,0.2)' :
                    '0 1px 4px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark' ?
                      '0 8px 24px rgba(0,0,0,0.3)' :
                      '0 4px 12px rgba(0,0,0,0.12)',
                    borderColor: theme.palette.mode === 'dark' ?
                      alpha(theme.palette.primary.main, 0.4) :
                      alpha(theme.palette.primary.main, 0.3)
                  }
                }}
                onClick={(e) => {
                  // Prevent navigation if clicking on action buttons
                  if (!e.currentTarget.contains(e.target) || 
                      e.target.closest('.action-button-container') !== null) {
                    return;
                  }
                  navigate(`/projects/${project._id}`);
                }}
              >
                {/* Color Bar at Top */}
                <Box 
                  sx={{ 
                    height: 3, 
                    width: '100%', 
                    bgcolor: getPhaseColor(project.phase),
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1
                  }} 
                />
                
                {/* Project Header */}
                <Box sx={{ p: 1.5, pb: 1, pt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ maxWidth: '80%' }}>
                      <Typography 
                        variant="h5" 
                        component="div" 
                        sx={{ 
                          mb: 0.5,
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: 1.2,
                          color: 'text.primary'
                        }}
                      >
                        {project.projectName}
                      </Typography>
                      {project.logline && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            lineHeight: 1.35,
                            height: 34,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {project.logline}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={project.status === 'active' ? 'Active' : 'On Hold'}
                      icon={project.status === 'active' ? <PlayCircleIcon sx={{ fontSize: '0.9rem' }} /> : <StopIcon sx={{ fontSize: '0.9rem' }} />}
                      sx={{ 
                        fontWeight: 600,
                        borderRadius: 1,
                        px: 0.5,
                        background: project.status === 'active' ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1),
                        color: project.status === 'active' ? '#4caf50' : '#ff9800',
                        border: `1px solid ${project.status === 'active' ? alpha('#4caf50', 0.2) : alpha('#ff9800', 0.2)}`,
                        height: 24
                      }}
                    />
                  </Box>
                </Box>
                
                <Divider sx={{ mx: 1.5 }} />
                
                {/* Project Details */}
                <Box sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Tags */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 22 }}>
                    <Chip 
                      size="small" 
                      label={project.phase}
                      sx={{ 
                        borderRadius: 1,
                        backgroundColor: getPhaseColor(project.phase),
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24
                      }}
                    />
                    <Chip 
                      size="small" 
                      label={project.genre}
                      variant="outlined"
                      sx={{ 
                        borderRadius: 1,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24
                      }}
                    />
                    {project.projectType && (
                      <Chip 
                        size="small"
                        label={projectTypes.find(t => t.value === project.projectType)?.label.replace(' Project', '')}
                        variant="outlined"
                        sx={{ 
                          borderRadius: 1,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          height: 24
                        }}
                      />
                    )}
                  </Box>
                  
                  {/* Progress Bar */}
                  {project.progress !== undefined && (
                    <Box sx={{ mb: 1.5 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={project.progress || 0} 
                        sx={{
                          height: 4,
                          borderRadius: 1
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Academic Info - Enhanced */}
                  {project.projectType !== 'professional' && project.academicInfo && (
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5, 
                      flexWrap: 'wrap', 
                      mb: 1.5
                    }}>
                      {project.academicInfo.institution && (
                        <Chip
                          size="small"
                          icon={<SchoolIcon sx={{ fontSize: '0.9rem' }} />}
                          label={project.academicInfo.institution}
                          sx={{
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24
                          }}
                        />
                      )}
                      {project.academicInfo.department && (
                        <Chip
                          size="small"
                          label={project.academicInfo.department}
                          sx={{
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            color: 'text.secondary',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24
                          }}
                        />
                      )}
                      {(project.academicInfo.term || project.academicInfo.gradYear) && (
                        <Chip
                          size="small"
                          label={`${project.academicInfo.term || ''} ${project.academicInfo.gradYear || ''}`.trim()}
                          sx={{
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            color: 'text.secondary',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Professional Info - Enhanced */}
                  {project.projectType === 'professional' && (
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5, 
                      flexWrap: 'wrap', 
                      mb: 1.5
                    }}>
                      {project.budget && (
                        <Chip
                          size="small"
                          icon={<AttachMoneyIcon sx={{ fontSize: '0.9rem' }} />}
                          label={(() => {
                            const num = typeof project.budget === 'number'
                              ? project.budget
                              : parseFloat(String(project.budget).replace(/[^0-9.]/g, ''));
                            return isNaN(num)
                              ? project.budget
                              : num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
                          })()}
                          sx={{
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24
                          }}
                        />
                      )}
                      {project.teamSize && (
                        <Chip
                          size="small"
                          icon={<GroupIcon sx={{ fontSize: '0.9rem' }} />}
                          label={project.teamSize}
                          sx={{
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Footer Info */}
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      {project.director && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BadgeIcon sx={{ 
                            fontSize: '0.9rem', 
                            color: theme.palette.mode === 'dark' ? 
                              alpha(theme.palette.warning.light, 0.9) : 
                              alpha(theme.palette.warning.main, 0.7) 
                          }} />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme.palette.mode === 'dark' ? 
                                alpha(theme.palette.common.white, 0.85) : 
                                theme.palette.text.secondary,
                              fontWeight: 500 
                            }}
                          >
                            {project.director}
                          </Typography>
                        </Box>
                      )}
                      {project.createdAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <ScheduleIcon sx={{ 
                            fontSize: '0.9rem', 
                            color: theme.palette.mode === 'dark' ? 
                              alpha(theme.palette.info.light, 0.9) : 
                              alpha(theme.palette.info.main, 0.7) 
                          }} />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme.palette.mode === 'dark' ? 
                                alpha(theme.palette.common.white, 0.85) : 
                                theme.palette.text.secondary,
                              fontWeight: 500 
                            }}
                          >
                            {new Date(project.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Box className="action-button-container" sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Project">
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? 
                              theme.palette.primary.light : 
                              theme.palette.primary.main,
                            bgcolor: theme.palette.mode === 'dark' ? 
                              alpha(theme.palette.primary.main, 0.15) : 
                              alpha(theme.palette.primary.main, 0.08),
                            backdropFilter: theme.palette.mode === 'dark' ? 'blur(4px)' : 'none',
                            border: theme.palette.mode === 'dark' ? 
                              `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : 
                              'none',
                            boxShadow: theme.palette.mode === 'dark' ? 
                              `0 2px 6px ${alpha(theme.palette.common.black, 0.2)}` : 
                              'none',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 
                                alpha(theme.palette.primary.main, 0.25) : 
                                alpha(theme.palette.primary.main, 0.12),
                              boxShadow: theme.palette.mode === 'dark' ? 
                                `0 3px 8px ${alpha(theme.palette.common.black, 0.3)}` : 
                                'none'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${project._id}/edit`);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Project">
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? 
                              theme.palette.error.light : 
                              theme.palette.error.main,
                            bgcolor: theme.palette.mode === 'dark' ? 
                              alpha(theme.palette.error.main, 0.15) : 
                              alpha(theme.palette.error.main, 0.08),
                            backdropFilter: theme.palette.mode === 'dark' ? 'blur(4px)' : 'none',
                            border: theme.palette.mode === 'dark' ? 
                              `1px solid ${alpha(theme.palette.error.main, 0.2)}` : 
                              'none',
                            boxShadow: theme.palette.mode === 'dark' ? 
                              `0 2px 6px ${alpha(theme.palette.common.black, 0.2)}` : 
                              'none',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 
                                alpha(theme.palette.error.main, 0.25) : 
                                alpha(theme.palette.error.main, 0.12),
                              boxShadow: theme.palette.mode === 'dark' ? 
                                `0 3px 8px ${alpha(theme.palette.common.black, 0.3)}` : 
                                'none'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setProjectToDelete(project);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      {/* Create Project Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="md" 
        fullWidth
        aria-labelledby="create-project-dialog-title"
        aria-describedby="create-project-dialog-description"
        // Ensure proper focus management
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disablePortal={false}
      >
        <DialogTitle id="create-project-dialog-title">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6" component="div">
              Create New Project
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Select project type to see relevant fields
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent id="create-project-dialog-description">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="project-type-label">Project Type</InputLabel>
                <Select
                  labelId="project-type-label"
                  id="project-type"
                  name="projectType"
                  value={newProject.projectType}
                  label="Project Type"
                  onChange={handleInputChange}
                  autoFocus
                >
                  {projectTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="projectName"
                name="projectName"
                label="Project Name"
                value={newProject.projectName}
                onChange={handleInputChange}
                error={errors.some(e => e.includes('projectName'))}
                helperText={errors.find(e => e.includes('projectName'))?.split(': ')[1]}
              />
            </Grid>

            {/* Academic fields for non-professional projects */}
            {newProject.projectType !== 'professional' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="academicInfo.institution"
                    name="academicInfo.institution"
                    label="Institution"
                    value={newProject.academicInfo.institution}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="academicInfo.department"
                    name="academicInfo.department"
                    label="Department"
                    value={newProject.academicInfo.department}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="academicInfo.advisor"
                    name="academicInfo.advisor"
                    label="Advisor"
                    value={newProject.academicInfo.advisor}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="term-label">Term</InputLabel>
                    <Select
                      labelId="term-label"
                      id="academicInfo.term"
                      name="academicInfo.term"
                      value={newProject.academicInfo.term}
                      label="Term"
                      onChange={handleInputChange}
                    >
                      {['Fall', 'Winter', 'Spring', 'Summer'].map((term) => (
                        <MenuItem key={term} value={term}>
                          {term}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="academicInfo.gradYear"
                    name="academicInfo.gradYear"
                    label="Graduation Year"
                    value={newProject.academicInfo.gradYear}
                    onChange={handleInputChange}
                  />
                </Grid>
              </>
            )}

            {/* Common fields */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="genre-label">Genre</InputLabel>
                <Select
                  labelId="genre-label"
                  id="genre"
                  name="genre"
                  value={newProject.genre}
                  label="Genre"
                  onChange={handleInputChange}
                  error={errors.some(e => e.includes('genre'))}
                >
                  {genres.map((genre) => (
                    <MenuItem key={genre} value={genre}>
                      {genre}
                    </MenuItem>
                  ))}
                </Select>
                {errors.some(e => e.includes('genre')) && (
                  <FormHelperText error>
                    {errors.find(e => e.includes('genre'))?.split(': ')[1]}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                id="logline"
                name="logline"
                label="Logline"
                value={newProject.logline}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="synopsis"
                name="synopsis"
                label="Synopsis"
                value={newProject.synopsis}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="phase-label">Phase</InputLabel>
                <Select
                  labelId="phase-label"
                  id="phase"
                  name="phase"
                  value={newProject.phase}
                  label="Phase"
                  onChange={handleInputChange}
                >
                  {phases.map((phase) => (
                    <MenuItem key={phase} value={phase}>
                      {phase}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Professional fields */}
            {newProject.projectType === 'professional' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="budget"
                    name="budget"
                    label="Budget"
                    value={newProject.budget}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="teamSize"
                    name="teamSize"
                    label="Team Size"
                    value={newProject.teamSize}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="director"
                    name="director"
                    label="Director"
                    value={newProject.director}
                    onChange={handleInputChange}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateProject}
            variant="contained"
            color="primary"
          >
            Create Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{projectToDelete?.projectName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Projects;
